import { useState, useEffect, useCallback, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Factory, ArrowLeft, ScanLine, CheckCircle2, AlertTriangle, Loader2, ChevronRight, Hammer,
} from "lucide-react";
import { listPresses, pressConsumeSFG, listPressConsumptions, checkCoil } from "@/lib/api";
import { fmtKg } from "@/lib/utils";
import BarcodeScanner from "@/components/BarcodeScanner";
import { Button } from "@/components/ui/button";

const LS_LAST_PRESS = "sre.floor.lastPress";

const beep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.frequency.value = 880;
    g.gain.value = 0.12;
    o.start();
    setTimeout(() => { o.stop(); ctx.close(); }, 110);
  } catch (err) { if (process.env.NODE_ENV !== "production") console.debug("beep unavailable", err); }
};

// ---------------- Step 1: pick a press ---------------------
const PressPicker = ({ presses, onPick, lastPick }) => (
  <div data-testid="press-picker" className="space-y-6">
    <div>
      <p className="text-[11px] tracking-[0.3em] uppercase text-slate-500 font-semibold mb-2">Step 1 of 3</p>
      <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">Which press are you using?</h2>
      <p className="text-base text-slate-600 mt-2">Tap the press you&apos;re standing in front of.</p>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
      {presses.map((p) => {
        const isLast = lastPick === p.press_id;
        return (
          <button
            key={p.press_id}
            data-testid={`press-tile-${p.press_id}`}
            onClick={() => onPick(p.press_id)}
            className={`relative aspect-square rounded-sm border-2 transition-all flex flex-col items-center justify-center gap-3 active:scale-95 ${
              isLast
                ? "border-slate-900 bg-slate-900 text-white shadow-lg"
                : "border-slate-300 bg-white hover:border-slate-900 hover:shadow-md text-slate-900"
            }`}
          >
            <Hammer className={`h-12 w-12 ${isLast ? "text-white" : "text-slate-400"}`} />
            <span className="font-heading text-2xl sm:text-3xl font-bold tracking-wide">{p.label.toUpperCase()}</span>
            {isLast && (
              <span className="absolute top-2 right-2 text-[9px] tracking-widest uppercase bg-white text-slate-900 px-1.5 py-0.5 rounded-sm font-semibold">last used</span>
            )}
          </button>
        );
      })}
    </div>
  </div>
);

// ---------------- Step 2: scan / enter SFG ---------------------
const SfgPicker = ({ pressId, onSwitchPress, onSfgFound }) => {
  const [scannerActive, setScannerActive] = useState(true);
  const [manualId, setManualId] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const lookup = useCallback(async (text) => {
    setBusy(true);
    setError("");
    try {
      const c = await checkCoil(text.trim());
      if (!c.exists) {
        setError(`No coil found with ID "${text}"`);
        setScannerActive(true);
        return;
      }
      const coil = c.coil;
      if (coil.kind !== "sfg") {
        setError(`${coil.coil_id} is not an SFG (it's a ${coil.kind === "mother" ? "RM" : coil.kind}). Only SFG coils can be consumed on a press.`);
        setScannerActive(true);
        return;
      }
      if (coil.status === "consumed") {
        setError(`${coil.coil_id} was already fully consumed${coil.consumed_at ? ` on ${coil.consumed_at.slice(0, 10)}` : ""}.`);
        setScannerActive(true);
        return;
      }
      if (coil.status === "dispatched") {
        setError(`${coil.coil_id} has already been dispatched.`);
        setScannerActive(true);
        return;
      }
      if (coil.status !== "received") {
        setError(`${coil.coil_id} is not available (status: ${coil.status}).`);
        setScannerActive(true);
        return;
      }
      beep();
      setScannerActive(false);
      onSfgFound(coil);
    } catch (e) {
      setError(e?.response?.data?.detail || "Lookup failed — try again");
      setScannerActive(true);
    } finally {
      setBusy(false);
    }
  }, [onSfgFound]);

  const handleManual = (e) => {
    e.preventDefault();
    if (!manualId.trim()) return;
    lookup(manualId.trim());
    setManualId("");
  };

  return (
    <div data-testid="sfg-picker" className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] tracking-[0.3em] uppercase text-slate-500 font-semibold mb-1">Step 2 of 3</p>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Scan the SFG coil</h2>
          <p className="text-sm text-slate-600 mt-1">On <span className="font-bold text-slate-900">{pressId.replace("-", " ")}</span> — point camera at the SFG barcode or type the ID.</p>
        </div>
        <Button
          variant="outline"
          onClick={onSwitchPress}
          data-testid="switch-press-btn"
          className="h-12 px-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Change press
        </Button>
      </div>

      <div className="max-w-sm mx-auto w-full">
        <BarcodeScanner active={scannerActive && !busy} onDetected={lookup} />
      </div>

      <form onSubmit={handleManual} className="flex gap-2 bg-white border border-slate-200 rounded-sm p-3">
        <input
          data-testid="manual-sfg-input"
          value={manualId}
          onChange={(e) => setManualId(e.target.value)}
          placeholder="Or type SR SFG ID (e.g. SFG-1)"
          className="flex-1 h-12 px-3 text-base font-mono border border-slate-300 rounded-sm focus:outline-none focus:border-slate-900"
          autoComplete="off"
          autoCapitalize="characters"
        />
        <Button data-testid="manual-sfg-submit" type="submit" disabled={!manualId.trim() || busy} className="h-12 px-5 bg-slate-900 hover:bg-slate-800 text-white">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanLine className="h-4 w-4 mr-1.5" />}
          Look up
        </Button>
      </form>

      {error && (
        <div data-testid="sfg-error" className="bg-red-50 border-2 border-red-300 rounded-sm p-4 flex items-start gap-3">
          <AlertTriangle className="h-6 w-6 text-red-700 shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-red-900 text-base">Cannot use this coil</p>
            <p className="text-sm text-red-800 mt-0.5">{error}</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------- Step 3: full / partial ---------------------
const FULL_THRESHOLD_KG = 10;

const ConsumeDecision = ({ pressId, sfg, onCancel, onDone }) => {
  const [mode, setMode] = useState(null); // null | "full" | "partial"
  const [balanceStr, setBalanceStr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const balance = useMemo(() => {
    const v = parseFloat(balanceStr);
    return Number.isFinite(v) ? v : NaN;
  }, [balanceStr]);

  const consumed = Number.isFinite(balance) ? Math.max(0, sfg.weight_kg - balance) : null;
  const balanceTooBig = Number.isFinite(balance) && balance >= sfg.weight_kg;
  const willAutoFull = Number.isFinite(balance) && balance >= 0 && balance < FULL_THRESHOLD_KG;
  const canSubmitPartial = Number.isFinite(balance) && balance >= 0 && !balanceTooBig;

  const submit = async (chosenMode) => {
    setSubmitting(true);
    try {
      const payload = { sfg_coil_id: sfg.coil_id, mode: chosenMode };
      if (chosenMode === "partial") payload.balance_kg = balance;
      const res = await pressConsumeSFG(pressId, payload);
      onDone(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save — try again");
    } finally {
      setSubmitting(false);
    }
  };

  // Numeric keypad helpers
  const keypadTap = (v) => {
    if (submitting) return;
    if (v === "back") return setBalanceStr((s) => s.slice(0, -1));
    if (v === "clear") return setBalanceStr("");
    if (v === ".") {
      if (balanceStr.includes(".")) return;
      return setBalanceStr((s) => (s === "" ? "0." : s + "."));
    }
    setBalanceStr((s) => (s.length >= 7 ? s : s + v));
  };

  return (
    <div data-testid="consume-decision" className="space-y-5">
      <div>
        <p className="text-[11px] tracking-[0.3em] uppercase text-slate-500 font-semibold mb-1">Step 3 of 3</p>
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">How much was used?</h2>
      </div>

      {/* SFG summary card */}
      <div data-testid="sfg-summary" className="bg-white border-2 border-slate-900 rounded-sm p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold">SR SFG ID</p>
            <p className="font-mono text-2xl sm:text-3xl font-bold text-slate-900 mt-0.5">{sfg.coil_id}</p>
          </div>
          <span className="inline-flex items-center text-[10px] tracking-wider uppercase font-semibold px-2 py-1 rounded-sm bg-orange-50 text-orange-800 border border-orange-200">SFG</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4 text-sm">
          {[
            ["Weight", `${fmtKg(sfg.weight_kg)} kg`],
            ["Grade", sfg.grade || "—"],
            ["Width", sfg.width_mm ? `${sfg.width_mm} mm` : "—"],
            ["From RM", sfg.mother_coil_id || "—"],
          ].map(([k, v]) => (
            <div key={k} className="bg-slate-50 px-3 py-2 rounded-sm">
              <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">{k}</p>
              <p className="font-mono text-sm sm:text-base text-slate-900 mt-0.5 font-bold">{v}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Mode select — two giant buttons */}
      {mode === null && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            data-testid="mode-full-btn"
            onClick={() => setMode("full")}
            className="aspect-[5/2] sm:aspect-[3/2] rounded-sm bg-green-600 hover:bg-green-700 active:bg-green-800 text-white flex flex-col items-center justify-center gap-2 transition-colors"
          >
            <CheckCircle2 className="h-14 w-14" />
            <span className="font-heading text-2xl sm:text-3xl font-bold">USED FULLY</span>
            <span className="text-xs sm:text-sm opacity-90">Entire {fmtKg(sfg.weight_kg)} kg consumed</span>
          </button>
          <button
            data-testid="mode-partial-btn"
            onClick={() => setMode("partial")}
            className="aspect-[5/2] sm:aspect-[3/2] rounded-sm bg-orange-600 hover:bg-orange-700 active:bg-orange-800 text-white flex flex-col items-center justify-center gap-2 transition-colors"
          >
            <Hammer className="h-14 w-14" />
            <span className="font-heading text-2xl sm:text-3xl font-bold">USED PARTIALLY</span>
            <span className="text-xs sm:text-sm opacity-90">Some left over — enter balance</span>
          </button>
        </div>
      )}

      {/* Full-confirm */}
      {mode === "full" && (
        <div data-testid="confirm-full-panel" className="bg-green-50 border-2 border-green-600 rounded-sm p-5">
          <p className="font-heading text-xl font-bold text-green-900">Confirm: entire SFG fully consumed?</p>
          <p className="text-sm text-green-800 mt-1">
            <span className="font-mono font-bold">{sfg.coil_id}</span> ({fmtKg(sfg.weight_kg)} kg) will be marked CONSUMED on {pressId.replace("-", " ")}. This cannot be undone.
          </p>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" onClick={() => setMode(null)} disabled={submitting} className="h-12 px-5">
              No, go back
            </Button>
            <Button
              data-testid="confirm-full-btn"
              onClick={() => submit("full")}
              disabled={submitting}
              className="h-12 px-6 bg-green-600 hover:bg-green-700 text-white text-base font-bold"
            >
              {submitting ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Saving…</> : <>Yes — fully consumed <ChevronRight className="h-5 w-5 ml-1" /></>}
            </Button>
          </div>
        </div>
      )}

      {/* Partial input */}
      {mode === "partial" && (
        <div data-testid="partial-panel" className="bg-white border-2 border-orange-500 rounded-sm p-5 space-y-4">
          <div>
            <p className="text-[11px] tracking-[0.25em] uppercase text-slate-600 font-semibold">Balance weight remaining</p>
            <p className="text-xs text-slate-500 mt-0.5">How many kg are LEFT after the press run?</p>
          </div>

          <div className="text-center bg-slate-50 border border-slate-200 rounded-sm py-5">
            <p className="font-mono text-5xl sm:text-6xl font-bold text-slate-900 tracking-tight" data-testid="balance-display">
              {balanceStr || "0"}
              <span className="text-2xl sm:text-3xl text-slate-500 ml-2">kg</span>
            </p>
          </div>

          {/* Live math */}
          {Number.isFinite(balance) && (
            <div className={`text-center text-sm font-semibold ${balanceTooBig ? "text-red-700" : willAutoFull ? "text-green-700" : "text-slate-700"}`} data-testid="partial-math">
              {balanceTooBig
                ? `❌ Balance ${balance} kg ≥ original ${fmtKg(sfg.weight_kg)} kg. Enter a smaller number.`
                : willAutoFull
                  ? `Balance under ${FULL_THRESHOLD_KG} kg → will mark as FULLY consumed`
                  : `Consumed: ${consumed.toFixed(1)} kg  ·  Balance: ${balance.toFixed(1)} kg`}
            </div>
          )}

          {/* Numeric keypad */}
          <div className="grid grid-cols-3 gap-2">
            {["1","2","3","4","5","6","7","8","9",".","0","back"].map((k) => (
              <button
                key={k}
                data-testid={`keypad-${k}`}
                onClick={() => keypadTap(k)}
                disabled={submitting}
                className="h-14 sm:h-16 rounded-sm bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-2xl font-bold text-slate-900 transition-colors disabled:opacity-50"
              >
                {k === "back" ? "⌫" : k}
              </button>
            ))}
          </div>

          <div className="flex gap-3 pt-1">
            <Button
              variant="outline"
              onClick={() => { setMode(null); setBalanceStr(""); }}
              disabled={submitting}
              data-testid="partial-back-btn"
              className="h-12 px-5 flex-1"
            >
              Back
            </Button>
            <Button
              data-testid="confirm-partial-btn"
              onClick={() => submit("partial")}
              disabled={!canSubmitPartial || submitting}
              className="h-12 px-6 flex-1 bg-orange-600 hover:bg-orange-700 text-white text-base font-bold"
            >
              {submitting ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Saving…</> : <>Save consumption</>}
            </Button>
          </div>
        </div>
      )}

      {mode === null && (
        <Button variant="ghost" onClick={onCancel} data-testid="cancel-decision-btn" className="text-slate-500">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Scan a different SFG
        </Button>
      )}
    </div>
  );
};

// ---------------- Step 4: success screen ---------------------
const SuccessFlash = ({ result, pressId, onContinue }) => {
  const [countdown, setCountdown] = useState(4);
  useEffect(() => {
    if (countdown <= 0) { onContinue(); return; }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown, onContinue]);

  const { consumption, sfg } = result;
  const fully = consumption.mode === "full";

  return (
    <div data-testid="success-flash" className="bg-white border-2 border-green-600 rounded-sm p-6 sm:p-10 text-center">
      <CheckCircle2 className="h-20 w-20 text-green-600 mx-auto" />
      <p className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mt-4">Saved!</p>
      <p className="text-base text-slate-700 mt-2">
        <span className="font-mono font-bold">{consumption.sfg_coil_id}</span> on {pressId.replace("-", " ")}
      </p>
      <div className="grid grid-cols-3 gap-3 mt-6 max-w-xl mx-auto">
        <div className="bg-slate-50 rounded-sm px-3 py-3">
          <p className="text-[9px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Consumed</p>
          <p className="font-mono text-xl font-bold text-slate-900 mt-1">{fmtKg(consumption.weight_consumed_kg)} kg</p>
        </div>
        <div className="bg-slate-50 rounded-sm px-3 py-3">
          <p className="text-[9px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Balance</p>
          <p className={`font-mono text-xl font-bold mt-1 ${fully ? "text-slate-400" : "text-orange-700"}`}>{consumption.balance_kg} kg</p>
        </div>
        <div className="bg-slate-50 rounded-sm px-3 py-3">
          <p className="text-[9px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Status</p>
          <p className={`font-bold text-sm uppercase tracking-wider mt-1.5 ${fully ? "text-slate-700" : "text-orange-700"}`}>
            {fully ? "Fully used" : "Partial · still in stock"}
          </p>
        </div>
      </div>
      <Button
        onClick={onContinue}
        data-testid="continue-btn"
        className="mt-6 h-14 px-8 bg-slate-900 hover:bg-slate-800 text-white text-lg font-bold"
      >
        Scan next SFG ({countdown})
      </Button>
    </div>
  );
};

// ---------------- Recent strip ---------------------
const RecentStrip = ({ pressId }) => {
  const { data: recent } = useQuery({
    queryKey: ["press-consumptions", pressId],
    queryFn: () => listPressConsumptions(pressId, 5),
    enabled: !!pressId,
    refetchInterval: 15000,
  });
  if (!recent || recent.length === 0) return null;
  return (
    <div data-testid="recent-strip" className="bg-white border border-slate-200 rounded-sm p-3 mt-6">
      <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold mb-2">Last 5 on {pressId.replace("-", " ")}</p>
      <ul className="text-xs space-y-1">
        {recent.map((r) => (
          <li key={r.id} className="flex items-center justify-between gap-3 border-b border-slate-100 last:border-0 pb-1 last:pb-0">
            <span className="font-mono font-semibold text-slate-900">{r.sfg_coil_id}</span>
            <span className="text-slate-500">{fmtKg(r.weight_consumed_kg)} kg · {r.mode}</span>
            <span className="text-slate-400">{(r.created_at || "").slice(11, 16)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

// ---------------- Page root ---------------------
const ProductionFloor = () => {
  const qc = useQueryClient();
  const { data: presses, isLoading: pressesLoading } = useQuery({
    queryKey: ["presses"],
    queryFn: listPresses,
  });
  const [pressId, setPressId] = useState(null);
  const [sfg, setSfg] = useState(null);
  const [successResult, setSuccessResult] = useState(null);

  const pickPress = (pid) => {
    localStorage.setItem(LS_LAST_PRESS, pid);
    setPressId(pid);
  };

  const handleConsumed = (res) => {
    setSuccessResult(res);
    setSfg(null);
    qc.invalidateQueries({ queryKey: ["stats"] });
    qc.invalidateQueries({ queryKey: ["coils"] });
    qc.invalidateQueries({ queryKey: ["press-consumptions", pressId] });
  };

  return (
    <div data-testid="production-floor-page" className="max-w-4xl mx-auto">
      <div className="border-b border-slate-200 pb-4 mb-6 flex items-center gap-3">
        <Factory className="h-7 w-7 text-slate-900" />
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-slate-500 font-semibold">Slit Coil Usage</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Press Consumption</h1>
        </div>
      </div>

      {pressesLoading && (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
      )}

      {!pressesLoading && !pressId && presses && (
        <PressPicker presses={presses} onPick={pickPress} lastPick={localStorage.getItem(LS_LAST_PRESS)} />
      )}

      {pressId && !sfg && !successResult && (
        <>
          <SfgPicker
            pressId={pressId}
            onSwitchPress={() => setPressId(null)}
            onSfgFound={setSfg}
          />
          <RecentStrip pressId={pressId} />
        </>
      )}

      {pressId && sfg && !successResult && (
        <ConsumeDecision
          pressId={pressId}
          sfg={sfg}
          onCancel={() => setSfg(null)}
          onDone={handleConsumed}
        />
      )}

      {successResult && (
        <SuccessFlash
          result={successResult}
          pressId={pressId}
          onContinue={() => setSuccessResult(null)}
        />
      )}
    </div>
  );
};

export default ProductionFloor;
